import React, { useEffect, useState, useCallback } from "react";
import { api, formatError } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowUp, ArrowDown, Plus } from "lucide-react";
import AdminEComicsVachana from "./AdminEComicsVachana";

const publishError = (e) => {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object" && d.missing) {
    return `Cannot publish. Missing:\n- ${d.missing.join("\n- ")}`;
  }
  return formatError(e);
};

export default function AdminEComicsVolume({ volume, onBack }) {
  const [vachanas, setVachanas] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ sequence_number: "", kannada_title: "", english_title: "" });
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/ecomics/admin/volumes/${volume.volume_id}/vachanas`);
      setVachanas(data);
    } catch (e) { toast.error(formatError(e)); }
  }, [volume.volume_id]);
  useEffect(() => { load(); }, [load]);

  const createVachana = async () => {
    if (!form.sequence_number || !form.kannada_title || !form.english_title) {
      toast.error("Sequence number, Kannada title and English title are required."); return;
    }
    try {
      await api.post(`/ecomics/admin/volumes/${volume.volume_id}/vachanas`, {
        sequence_number: parseInt(form.sequence_number, 10),
        kannada_title: form.kannada_title,
        english_title: form.english_title,
      });
      toast.success("Vachana created (Draft)");
      setForm({ sequence_number: "", kannada_title: "", english_title: "" });
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/ecomics/admin/vachanas/${editing.vachana_id}`, {
        sequence_number: parseInt(editing.sequence_number, 10),
        kannada_title: editing.kannada_title,
        english_title: editing.english_title,
      });
      toast.success("Vachana updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const setStatus = async (v, status) => {
    try {
      await api.post(`/ecomics/admin/vachanas/${v.vachana_id}/status`, { status });
      toast.success(`Vachana ${status}`);
      load();
    } catch (e) { toast.error(publishError(e)); }
  };

  const move = async (index, dir) => {
    const other = index + dir;
    if (other < 0 || other >= vachanas.length) return;
    const a = vachanas[index], b = vachanas[other];
    try {
      await api.put("/ecomics/admin/vachanas/reorder", { items: [
        { id: a.vachana_id, display_order: b.display_order },
        { id: b.vachana_id, display_order: a.display_order },
      ]});
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  if (selected) {
    return <AdminEComicsVachana vachanaId={selected.vachana_id} onBack={() => { setSelected(null); load(); }} />;
  }

  return (
    <div className="space-y-5" data-testid="admin-ecomics-volume">
      <button onClick={onBack} className="bb-link inline-flex items-center gap-1 text-sm" data-testid="ecomics-back-volumes"><ArrowLeft size={14}/> All volumes</button>
      <div>
        <div className="bb-overline text-[var(--bb-muted)]">VOLUME {volume.volume_number}</div>
        <h3 className="font-serif text-2xl text-[var(--bb-forest)]">{volume.title} — Vachanas</h3>
      </div>

      <div className="bb-card p-5">
        <div className="bb-overline text-[var(--bb-muted)] mb-3 flex items-center gap-2"><Plus size={13}/> ADD VACHANA</div>
        <div className="grid sm:grid-cols-4 gap-3">
          <input type="number" min="1" placeholder="Sequence #" value={form.sequence_number} onChange={e => setForm({ ...form, sequence_number: e.target.value })} className="bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] text-sm" data-testid="ecomics-vach-seq"/>
          <input placeholder="Kannada title" value={form.kannada_title} onChange={e => setForm({ ...form, kannada_title: e.target.value })} className="bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] text-sm" data-testid="ecomics-vach-kn"/>
          <input placeholder="English title" value={form.english_title} onChange={e => setForm({ ...form, english_title: e.target.value })} className="bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] text-sm" data-testid="ecomics-vach-en"/>
          <button onClick={createVachana} className="bb-btn-primary !py-2 text-sm" data-testid="ecomics-vach-create">Add</button>
        </div>
      </div>

      {vachanas.length === 0 ? (
        <div className="bb-card p-10 text-center text-sm text-[var(--bb-muted)]" data-testid="ecomics-vachanas-empty">No vachanas yet in this volume.</div>
      ) : (
        <div className="space-y-3">
          {vachanas.map((v, i) => (
            <div key={v.vachana_id} className="bb-card p-4" data-testid={`ecomics-vachana-row-${v.vachana_id}`}>
              <div className="flex flex-wrap items-center gap-4">
                <div className="w-9 h-9 rounded-full bg-[var(--bb-terracotta)]/10 text-[var(--bb-terracotta)] grid place-items-center font-serif shrink-0">{v.sequence_number}</div>
                <div className="flex-1 min-w-[180px]">
                  <div className="font-medium text-[var(--bb-forest)]">{v.kannada_title}</div>
                  <div className="text-xs text-[var(--bb-muted)]">{v.english_title} · order {v.display_order}</div>
                </div>
                <span className={`bb-tag ${v.status === "published" ? "bb-tag-green" : ""}`}>{v.status}</span>
                <div className="flex items-center gap-2 text-xs">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 rounded border border-[var(--bb-border)] disabled:opacity-30" data-testid={`ecomics-vach-up-${v.vachana_id}`}><ArrowUp size={12}/></button>
                  <button onClick={() => move(i, 1)} disabled={i === vachanas.length - 1} className="p-1.5 rounded border border-[var(--bb-border)] disabled:opacity-30" data-testid={`ecomics-vach-down-${v.vachana_id}`}><ArrowDown size={12}/></button>
                  <button onClick={() => setEditing({ ...v })} className="bb-link" data-testid={`ecomics-vach-edit-${v.vachana_id}`}>Edit</button>
                  {v.status !== "published"
                    ? <button onClick={() => setStatus(v, "published")} className="bb-btn-secondary !py-1 !px-3 text-xs" data-testid={`ecomics-vach-publish-${v.vachana_id}`}>Publish</button>
                    : <button onClick={() => setStatus(v, "draft")} className="text-[var(--bb-terracotta)] hover:underline" data-testid={`ecomics-vach-unpublish-${v.vachana_id}`}>Unpublish</button>}
                  {v.status !== "archived" && <button onClick={() => setStatus(v, "archived")} className="text-[var(--bb-error)] hover:underline" data-testid={`ecomics-vach-archive-${v.vachana_id}`}>Archive</button>}
                  <button onClick={() => setSelected(v)} className="bb-btn-primary !py-1 !px-3 text-xs" data-testid={`ecomics-vach-manage-${v.vachana_id}`}>Assets & Panels →</button>
                </div>
              </div>
              {editing?.vachana_id === v.vachana_id && (
                <div className="mt-4 pt-4 border-t border-[var(--bb-border)] grid sm:grid-cols-4 gap-3">
                  <input type="number" value={editing.sequence_number} onChange={e => setEditing({ ...editing, sequence_number: e.target.value })} className="bg-transparent border-b border-[var(--bb-border)] py-2 outline-none text-sm" data-testid="ecomics-vach-edit-seq"/>
                  <input value={editing.kannada_title} onChange={e => setEditing({ ...editing, kannada_title: e.target.value })} className="bg-transparent border-b border-[var(--bb-border)] py-2 outline-none text-sm" data-testid="ecomics-vach-edit-kn"/>
                  <input value={editing.english_title} onChange={e => setEditing({ ...editing, english_title: e.target.value })} className="bg-transparent border-b border-[var(--bb-border)] py-2 outline-none text-sm" data-testid="ecomics-vach-edit-en"/>
                  <div className="flex gap-2">
                    <button onClick={saveEdit} className="bb-btn-primary !py-1.5 !px-3 text-xs" data-testid="ecomics-vach-edit-save">Save</button>
                    <button onClick={() => setEditing(null)} className="text-xs text-[var(--bb-muted)] hover:underline">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
